/**
 * Build valid facturation archive payloads for the AI agent.
 */

import i18n from '../../i18n';

const db = (key, opts) => i18n.t(`modules.shared.aiAgent.documentBuilder.${key}`, opts);

function todayIsoDate() {
  return new Date().toISOString().split('T')[0];
}

function addDaysFrom(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

async function loadDocumentPrefix(docType) {
  const defaults = { invoice: 'FAC', quote: 'DEV', proforma: 'PRO' };
  try {
    const settings = await localApiAdapter.get('/settings');
    const prefixes =
      settings?.configuration?.documentPrefixes ||
      settings?.invoicing?.documentPrefixes ||
      {};
    if (docType === 'invoice' && prefixes.invoice) return prefixes.invoice;
    if (docType === 'quote' && prefixes.quote) return prefixes.quote;
    if (docType === 'proforma' && prefixes.proforma) return prefixes.proforma;
  } catch {
    // use defaults
  }
  return defaults[docType] || 'FAC';
}

export async function generateArchiveDocumentNumber(docType = 'invoice') {
  const prefix = await loadDocumentPrefix(docType);

  let existing = [];
  try {
    const rows = await localApiAdapter.get('/facturation/archives');
    existing = Array.isArray(rows) ? rows : [];
  } catch {
    existing = [];
  }

  const normalizedType = docType.replace(/-/g, '_');
  const sequenceNumbers = existing
    .filter((doc) => {
      const t = String(doc.type || '').replace(/-/g, '_');
      return t === normalizedType;
    })
    .map((doc) => {
      if (!doc.number || !String(doc.number).startsWith(`${prefix}-`)) return 0;
      const part = String(doc.number).split('-').pop();
      const n = parseInt(part, 10);
      return Number.isFinite(n) ? n : 0;
    })
    .filter((n) => n > 0);

  const next = sequenceNumbers.length > 0 ? Math.max(...sequenceNumbers) + 1 : 1;
  return `${prefix}-${String(next).padStart(2, '0')}`;
}

export async function resolveFacturationSupplier(supplierName) {
  if (!supplierName || !String(supplierName).trim()) {
    return { supplierId: null, supplierLabel: '---' };
  }

  const rows = await localApiAdapter.get('/facturation/fournisseurs').catch(() => []);
  const suppliers = Array.isArray(rows) ? rows : [];
  const needle = String(supplierName).trim().toLowerCase();

  const match = suppliers.find((s) => {
    const label = (s.company_name || s.name || s.companyName || '').trim().toLowerCase();
    return label === needle || label.includes(needle) || needle.includes(label);
  });

  if (match) {
    return {
      supplierId: match.id,
      supplierLabel: match.company_name || match.name || match.companyName || supplierName,
    };
  }

  return { supplierId: null, supplierLabel: String(supplierName).trim() };
}

export async function resolveFacturationClient(clientName) {
  if (!clientName || !String(clientName).trim()) {
    return { clientId: null, clientLabel: '---' };
  }

  const rows = await localApiAdapter.get('/facturation/clients');
  const clients = Array.isArray(rows) ? rows : [];
  const needle = String(clientName).trim().toLowerCase();

  const match = clients.find((c) => {
    const label = (c.company_name || c.name || c.companyName || '').trim().toLowerCase();
    return label === needle || label.includes(needle) || needle.includes(label);
  });

  if (match) {
    return {
      clientId: match.id,
      clientLabel: match.company_name || match.name || match.companyName || clientName,
    };
  }

  return { clientId: null, clientLabel: String(clientName).trim() };
}

function normalizeLineItems(rawItems, amountFallback = 0) {
  if (Array.isArray(rawItems) && rawItems.length > 0) {
    return rawItems.map((item, index) => ({
      id: index + 1,
      name: item.name || item.product || db('line_item_name', { index: index + 1 }),
      quantity: Number(item.quantity) || 1,
      price: Number(item.price ?? item.unit_price ?? item.unitPrice) || 0,
    }));
  }

  const amount = Number(amountFallback) || 0;
  return [{ id: 1, name: db('service_line'), quantity: 1, price: amount }];
}

function computeInvoiceTotals(items, tvaRate = 19) {
  const subtotal = items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.price) || 0),
    0
  );
  const taxAmount = Math.round(subtotal * (tvaRate / 100) * 100) / 100;
  const amount = Math.round((subtotal + taxAmount) * 100) / 100;
  return { subtotal, taxAmount, amount };
}

/**
 * Full payload for POST /api/facturation/archives
 */
export async function buildInvoiceDocumentPayload(params = {}) {
  const clientName = params.client || params.clientName || params.name || db('default_client');
  const { clientId, clientLabel } = await resolveFacturationClient(clientName);
  const docType = params.type || 'invoice';
  const number = await generateArchiveDocumentNumber(docType);
  const date = todayIsoDate();
  const tvaRate = Number(params.tva_rate ?? params.tvaRate ?? 19);
  const items = normalizeLineItems(params.items, params.amount);
  const { subtotal, taxAmount, amount } = computeInvoiceTotals(items, tvaRate);

  return {
    type: docType,
    number,
    title: params.title || db('invoice_title', { client: clientLabel }),
    clientId,
    client: clientLabel,
    date,
    dueDate: params.dueDate || params.due_date || addDaysFrom(date, 30),
    status: 'draft',
    category: 'FACTURATION',
    items,
    subtotal,
    taxAmount,
    tax_amount: taxAmount,
    amount,
    showTva: true,
    tvaRate,
    tva_rate: tvaRate,
    notes: params.notes || i18n.t('modules.shared.aiAgent.localExecutor.created_via_agent'),
  };
}
